import { Injectable } from '@angular/core';
import * as moment from 'moment';
import { HttpClient } from '@angular/common/http';

import {
  EASYRX_ORDER_SUFFIX,
  EasyRxPharmacy,
  SendEasyRxScript,
  EasyRxMedicationPrescriptionDTO,
  EasyRxPharmacyListResponseDTO,
  SendEasyRxPrescriptionRequestDTO,
  SendEasyRxPrescriptionResponseDTO,
  EasyRxPrescriptionStatusResponseDTO,
} from './models/easyrx.model';
import { AddressVo, ClinicalNewClient, MedicationPrescription } from './api-client.service';
import { buildEscriptOrderNumber } from '@app/core/functions/escript-order-number';

/**
 * EasyRx e-script client. Talks ONLY to the Florence proxy (`api/v2/.../easyrx/*`), which holds the
 * OAuth2 credentials and forwards to EasyRx. Proxy responses wrap the raw EasyRx JSON as a string, so
 * every call parses the inner payload.
 *
 * Divergence from CompuAssist: status is a doctor-triggered fetch (no webhook / no Firestore stream).
 * The order number is CLIENT-generated (shared `HBC-…-ER` format) — EasyRx validates it
 * as required on send and echoes it back (probed live 2026-07-02; the spec's "server-assigned" note
 * was wrong).
 */
@Injectable({
  providedIn: 'root',
})
export class EasyRxApiService {
  constructor(
    private httpClient: HttpClient,
    private clinicalNewClient: ClinicalNewClient
  ) {}

  /** Generates the order number for a prescription (shared HBC-…-ER format). */
  getOrderNumber(prescription: MedicationPrescription): string {
    return buildEscriptOrderNumber(prescription, EASYRX_ORDER_SUFFIX);
  }

  /**
   * Sends a prescription to EasyRx via the Florence proxy. Returns the order number (client-generated,
   * required by EasyRx validation and echoed back in the response).
   */
  async sendPrescriptionToEasyRx(
    tenantId: string,
    prescription: MedicationPrescription,
    pharmacy: EasyRxPharmacy,
    pdfUrl: string,
    encounterId: string
  ): Promise<string> {
    const pdfBlob = await this.httpClient.get(pdfUrl, { responseType: 'blob' }).toPromise();
    const pdfBase64 = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve((reader.result as string).split(',')[1]);
      reader.onerror = reject;
      reader.readAsDataURL(pdfBlob);
    });

    const orderNumber = this.getOrderNumber(prescription);
    const payload: SendEasyRxPrescriptionRequestDTO = {
      prescription: this.buildPrescriptionPayload(prescription, orderNumber),
      scriptPdfBase64: pdfBase64,
      pharmacy,
    };

    const request: SendEasyRxScript = { ScriptRequest: JSON.stringify(payload) };
    const response = await this.clinicalNewClient.sendEasyRxScript(tenantId, encounterId, request).toPromise();

    const sendResponse = JSON.parse(response.Data.ScriptResponse) as SendEasyRxPrescriptionResponseDTO;
    if (!sendResponse.success) {
      throw new Error(sendResponse.message || 'EasyRx rejected the prescription.');
    }
    return sendResponse.orderNumber || orderNumber;
  }

  /**
   * Pulls the COMPLETE pharmacy catalogue from EasyRx via the Florence proxy, paging with
   * limit/offset until `totalCount` is reached. Used by the admin catalogue refresh — the picker
   * itself only ever reads our own Firestore store.
   */
  async getAllPharmacies(practiceId: string): Promise<EasyRxPharmacy[]> {
    const pageSize = 200;
    const all: EasyRxPharmacy[] = [];
    let offset = 0;
    let totalCount = Infinity;
    while (offset < totalCount) {
      const response = await this.clinicalNewClient.getEasyRxPharmacies(practiceId, pageSize, offset).toPromise();
      if (!response?.Data?.PharmaciesResponse) {
        throw new Error('EasyRx pharmacy list returned no data.');
      }
      const page = JSON.parse(response.Data.PharmaciesResponse) as EasyRxPharmacyListResponseDTO;
      if (!page.success) {
        throw new Error('EasyRx pharmacy list request failed.');
      }
      const pharmacies = page.pharmacies ?? [];
      all.push(...pharmacies);
      totalCount = page.totalCount ?? all.length;
      if (!pharmacies.length) {
        break; // defensive: empty page before totalCount reached — avoid an endless loop
      }
      offset += pharmacies.length;
    }
    return all;
  }

  /**
   * Doctor-triggered status fetch (on modal open + manual refresh). No interval polling.
   */
  async getPrescriptionStatus(practiceId: string, orderNumber: string): Promise<EasyRxPrescriptionStatusResponseDTO | null> {
    const response = await this.clinicalNewClient.getEasyRxPrescriptionStatus(practiceId, orderNumber).toPromise();
    if (!response?.Data?.StatusResponse) {
      return null;
    }
    return JSON.parse(response.Data.StatusResponse) as EasyRxPrescriptionStatusResponseDTO;
  }

  /**
   * Near-passthrough map of the app's `MedicationPrescription` onto EasyRx's camelCase DTO (the two
   * models mirror each other).
   */
  private buildPrescriptionPayload(prescription: MedicationPrescription, orderNumber: string): EasyRxMedicationPrescriptionDTO {
    const provider = prescription.Provider;
    const patient = prescription.Patient;
    const patientDetails = patient?.PatientDetails;
    const account = patient?.PatientAccountDetails;
    const mainMember = account?.MedicalAidMainMemberDetails;

    const iso = (d: Date | string | null | undefined): string => (d ? moment(d).toISOString() : '');
    const address = (a: AddressVo | null | undefined) => ({
      line1: a?.Line1 ?? '',
      line2: a?.Line2 ?? '',
      line3: a?.Line3 ?? '',
      code: a?.Code ?? '',
    });

    return {
      medicationPrescriptionId: prescription.MedicationPrescriptionId,
      issuedDate: iso(prescription.IssuedDate) || moment().toISOString(),
      name: prescription.Name ?? '',
      orderNumber,
      provider: {
        name: provider?.TreatingDoctorName ?? '',
        practiceNumber: provider?.TreatingDoctorPracticeNumber ?? '',
        emailAddress: provider?.EmailAddress ?? '',
        cellphoneNumber: provider?.CellphoneNumber ?? '',
        physicalAddress: '',
        postalAddress: '',
      },
      patientDetails: {
        title: patientDetails?.Title ?? '',
        firstName: patientDetails?.FirstName ?? '',
        surname: patientDetails?.Surname ?? '',
        dateOfBirth: iso(patientDetails?.DateOfBirth),
        identityNo: patientDetails?.IdentityNo ?? '',
        passportNo: patientDetails?.PassportNo ?? '',
        country: patientDetails?.Country ?? '',
        gender: patientDetails?.Gender ?? '',
        contactNo: patientDetails?.ContactNo ?? '',
        emailAddress: patientDetails?.EmailAddress ?? '',
        // addresses live on PatientVo, NOT PersonVo — the previous untyped access read them off
        // PatientDetails and silently sent empty addresses
        physicalAddress: address(patient?.PhysicalAddress),
        postalAddress: address(patient?.PostalAddress),
      },
      mainMemberDetails: {
        title: mainMember?.Title ?? '',
        firstName: mainMember?.FirstName ?? '',
        surname: mainMember?.Surname ?? '',
        dateOfBirth: iso(mainMember?.DateOfBirth),
        identityNo: mainMember?.IdentityNo ?? '',
        passportNo: mainMember?.PassportNo ?? '',
        country: mainMember?.Country ?? '',
        gender: mainMember?.Gender ?? '',
        contactNo: mainMember?.ContactNo ?? '',
        emailAddress: mainMember?.EmailAddress ?? '',
      },
      medicationPrescriptionLines: (prescription.MedicationPrescriptionLines ?? []).map((line, index) => ({
        nappiCode: line.NappiCode ?? '',
        nappiCode10: line.NappiCode10 ?? '',
        medicationDescription: line.MedicationDescription ?? '',
        quantity: line.Quantity ?? 0,
        dosageType: line.DosageType ?? '',
        dosageUnits: line.DosageUnits ?? 0,
        durationType: line.DurationType ?? '',
        durationUnit: line.DurationUnit ?? 0,
        periodType: line.PeriodType ?? '',
        periodUnit: line.PeriodUnit ?? 0,
        frequencyUnits: line.FrequencyUnits ?? 0,
        repeat: line.Repeat ?? 0,
        chronicIndicator: line.ChronicIndicator ?? false,
        allowGeneric: line.AllowGeneric ?? false,
        diagnosis: (line.Diagnosis ?? []).map(d => ({
          diagnosisCode: d.DiagnosisCode ?? '',
          diagnosisDescription: d.DiagnosisDescription ?? '',
        })),
        parameters: line.Parameters ?? {},
        lineNum: index + 1,
      })),
    };
  }
}
